import React, { useState } from "react";
import { UserProfile } from "../auth/session";
import { StatCard } from "../components/StatCard";
import { StatusBanner } from "../components/StatusBanner";

interface ParentDashboardProps {
  user: UserProfile;
}

interface ChildProfile {
  id: string;
  name: string;
  age: number;
  rating: number;
  coach: string;
  academy: string;
  batch: string;
  weeklyHours: number;
  recentGames: number;
  puzzleAccuracy: number;
  consentVerified: boolean;
}

const CHILDREN: ChildProfile[] = [
  {
    id: "child-01",
    name: "Arjun Kamalendran",
    age: 14,
    rating: 1540,
    coach: "Coach Ramesh S. (FIDE Trainer)",
    academy: "Tamil Nadu Chess Academy",
    batch: "Intermediate Batch A",
    weeklyHours: 8.5,
    recentGames: 12,
    puzzleAccuracy: 86,
    consentVerified: true,
  },
  {
    id: "child-02",
    name: "Meera Kamalendran",
    age: 9,
    rating: 1120,
    coach: "Coach Anandhi V.",
    academy: "Tamil Nadu Chess Academy",
    batch: "Junior Beginners Batch",
    weeklyHours: 4.0,
    recentGames: 6,
    puzzleAccuracy: 78,
    consentVerified: true,
  },
];

export function ParentDashboard({
  user,
}: ParentDashboardProps): React.JSX.Element {
  const [selectedChildId, setSelectedChildId] = useState(CHILDREN[0]!.id);
  const activeChild =
    CHILDREN.find((c) => c.id === selectedChildId) || CHILDREN[0]!;

  return (
    <div className="role-dashboard parent-dashboard">
      <div className="parent-welcome-banner">
        <div>
          <h2>Parent / Guardian Oversight</h2>
          <p>
            Welcome, {user.displayName}. Monitor learning progress, academy
            attendance, and safety controls for your children.
          </p>
        </div>
        <div
          className="child-selector-group"
          role="group"
          aria-label="Select child profile"
        >
          <span className="selector-label">Active Ward:</span>
          <div className="child-buttons">
            {CHILDREN.map((child) => (
              <button
                key={child.id}
                type="button"
                className={`child-btn ${selectedChildId === child.id ? "active" : ""}`}
                onClick={() => setSelectedChildId(child.id)}
                aria-pressed={selectedChildId === child.id}
              >
                {child.name} ({child.age} yrs)
              </button>
            ))}
          </div>
        </div>
      </div>

      <StatusBanner
        type="info"
        title="Minor Child Data Protection Active"
        message="Strict guardian controls, COPPA/GDPR-K alignment, and panankAI safety guardrails are continuously enforced for all minor profiles."
      />

      <div className="dashboard-content">
        <div className="stat-grid">
          <StatCard
            label="Current Rating"
            value={activeChild.rating}
            subtext="Official Zhatura FIDE-aligned rating"
            trend={{ direction: "up", value: "+24 pts" }}
            icon="♟"
          />
          <StatCard
            label="Weekly Training Time"
            value={`${activeChild.weeklyHours} hrs`}
            subtext="Recommended: 6-10 hrs / week"
            icon="⏱"
          />
          <StatCard
            label="Tactics Accuracy"
            value={`${activeChild.puzzleAccuracy}%`}
            subtext="Academy curriculum puzzles"
            trend={{ direction: "up", value: "+4%" }}
            icon="🎯"
          />
          <StatCard
            label="Games Played"
            value={activeChild.recentGames}
            subtext="Last 14 days"
            icon="⚔"
          />
        </div>

        <div className="dashboard-cards-grid">
          <div className="section-card">
            <h3>Academy & Coaching Link</h3>
            <div className="card-detail-list">
              <div className="card-detail-row">
                <span className="row-label">Enrolled Academy</span>
                <span className="row-val">
                  <strong>{activeChild.academy}</strong>
                </span>
              </div>
              <div className="card-detail-row">
                <span className="row-label">Assigned Coach</span>
                <span className="row-val">{activeChild.coach}</span>
              </div>
              <div className="card-detail-row">
                <span className="row-label">Batch & Group</span>
                <span className="row-val">{activeChild.batch}</span>
              </div>
              <div className="card-detail-row">
                <span className="row-label">Attendance Status</span>
                <span className="badge badge-success">96% Regular</span>
              </div>
            </div>
          </div>

          <div className="section-card">
            <h3>Safety, Privacy & Minor Protections</h3>
            <div className="card-detail-list">
              <div className="card-detail-row">
                <span className="row-label">Guardian Consent</span>
                <span className="badge badge-success">Verified & Active</span>
              </div>
              <div className="card-detail-row">
                <span className="row-label">My Zhatura Coach (AI)</span>
                <span className="row-val">
                  Safe Educational Mode (Minor Protected)
                </span>
              </div>
              <div className="card-detail-row">
                <span className="row-label">Chat & Direct Messages</span>
                <span className="row-val">Disabled for Minors Under 16</span>
              </div>
              <div className="card-detail-row">
                <span className="row-label">Screen Time Limit</span>
                <span className="row-val">Max 2.5 hours / day</span>
              </div>
            </div>
          </div>
        </div>

        <div className="section-card">
          <h3>Family Subscription & Entitlements</h3>
          <div className="family-plan-banner">
            <div>
              <h4>Zhatura Family Annual Pass (Tamil Nadu State Program)</h4>
              <p>
                Active until 31 December 2026 • Includes all 2 children,
                Stockfish 18 cloud analyses, and Coach Ramesh assignments.
              </p>
            </div>
            <span className="badge badge-accent">Active Subscription</span>
          </div>
        </div>
      </div>
    </div>
  );
}
